import { useEffect, useState, useRef, useContext } from 'react';
import Sidebar from './Sidebar.js';
import { Card, Image, Row, Col, Container, Form, Button, Modal } from 'react-bootstrap';
import Breadcrumb from './Breadcrumb.js';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import UserAddGeneral from './UserAddGeneral.js';
import api from './api/axiosInstance.js'; // Make sure it attaches token
import NavTabs from './NavTabs.js';
import icSucess from './assets/img/ic-celebration-circle.svg';
import UserAddRegion from './UserAddRegion.js';
import UserAddJob from './UserAddJob.js';
import UserAddCompany from './UserAddCompany.js';
import UserAddStudying from './UserAddStudying.js';
import UserAddOccupation from './UserAddOccupation.js';
import { UserContext } from './UserContext';
import { isAdminType, canManageUser, canonicalRole } from './utils/roleUtils.js';

const UserEditWorkPreferences = () => {
    const occupationRef = useRef(null); // ✅ to call validate from parent (optional
    const { id } = useParams();
    const navigate = useNavigate();
    const regionRef = useRef();
    // Form fields state
    const [errors, setErrors] = useState({});
    const [profileImage, setProfileImage] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [activityStatus, setActivityStatus] = useState(true);
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [savedModalTitle, setSavedModalTitle] = useState('Update successful');
    const [savedModalMessage, setSavedModalMessage] = useState('');

    const [selectedCountry, setSelectedCountry] = useState(null);

    // hide edit if not self edit and admin is trying to edit same or higher role admin
    const { user: actor } = useContext(UserContext);
    const actorRole = canonicalRole(actor?.role);
    const actorId = String(actor?._id || '');
    const [targetUserRole, setTargetUserRole] = useState('');

    const isSelfEdit = actorId && actorId === String(id);

    const hideEditSameOrHigher =
        !isSelfEdit &&
        isAdminType(actorRole) &&
        isAdminType(targetUserRole) &&
        !canManageUser(actorRole, targetUserRole);

    const [generalData, setGeneralData] = useState({
        profileImage: '',
        firstName: '',
        familyName: '',
        gender: '',
        company: '',
        companyRole: '',
        activityStatus: false,
    })

    const [regionData, setRegionData] = useState({
        countriesWorkPreferences: [],
        citiesWorkPreferences: [],
        languagesWorkPreferences: [],
    })

    const [jobPreferencesData, setJobPreferencesData] = useState({
        industryJobPreferences: [],
        employmentJobPreferences: '',
        fteJobPreferences: '',
        jobTypeJobPreferences: '',
    })

    const [companyPreferencesData, setCompanyPreferencesData] = useState({
        companyGlobalSize:  '',
        companyLocalSize:  '',
        companyRating:  '',
        fortune:  '',
    })

    const [studyingInfoData, setStudyingInfoData] = useState({
        currentDegree:  '',
        futureDegree:  '',
        plannedGraduationYear:  '',
    })

    const [occupationData, setOccupationData] = useState({
        occupations: [{ major: '', subMajor: '', minor: '', unit: '', skill: '' }]
    });

    const userNavItems = [
        { to: '/user-edit/:id', eventKey: 'userInfo', label: 'About' },
        { to: '/user-edit-academics/:id', eventKey: 'academicsInfo', label: 'Academics' },
        { to: '/user-edit-work-preferences/:id', eventKey: 'work', label: 'Work Preferences' },
        { to: '/user-edit-access/:id', eventKey: 'access', label: 'Access & Permissions' },
        { to: '/user-edit-history/:id', eventKey: 'history', label: 'History of changes' },
    ];

    const normalizeId = (x) => {
        if (!x) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'object') {
            if (x._id) return normalizeId(x._id);
            if (x.$oid) return String(x.$oid);
            if (x.id) return normalizeId(x.id);
            if (typeof x.toHexString === 'function') return x.toHexString();
            if (typeof x.toString === 'function' && x.toString !== Object.prototype.toString) {
                const s = x.toString();
                if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
            }
        }
        return '';
    };

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res = await api.get('/api/companies');
                setCompanies(res.data);
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };

        fetchCompanies();
    }, []);

    const [tickets, setTickets] = useState([]);
    useEffect(() => {
        const fetchTickets = async () => {
            try {
                // ✅ goes through interceptor; token auto-attached
                const res = await api.get('/api/tickets'); // ✅
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }   
        };
        fetchTickets();
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            setIsLoadingUser(true);
            setShowErrorMessage(false);
            setErrorText('');

            try {
                const res = await api.get(`/api/users/${id}`);
                const currentUser = res.data;

                if (!currentUser) {
                    setErrorText('User not found.');
                    setShowErrorMessage(true);
                    return;
                }

                // ✅ REQUIRED: used by hideEditSameOrHigher gating
                setTargetUserRole(canonicalRole(currentUser.role || ''));

                // Set general data
                setGeneralData({
                    profileImage: currentUser.profileImage || '',
                    firstName: currentUser.firstName || '',
                    familyName: currentUser.familyName || '',
                    gender: currentUser.gender || '',
                    profileImagePreview: currentUser.profileImage || '',
                    company: normalizeId(currentUser.company),
                    companyRole: currentUser.companyRole || '',
                    activityStatus: currentUser.activityStatus || false,
                });

                // ✅ keep these fields in consistent shapes (arrays where expected)
                setRegionData({
                    citiesWorkPreferences: Array.isArray(currentUser.citiesWorkPreferences)
                        ? currentUser.citiesWorkPreferences
                        : (currentUser.citiesWorkPreferences ? [currentUser.citiesWorkPreferences] : []),
                    countriesWorkPreferences: Array.isArray(currentUser.countriesWorkPreferences)
                        ? currentUser.countriesWorkPreferences
                        : (currentUser.countriesWorkPreferences ? [currentUser.countriesWorkPreferences] : []),
                    languagesWorkPreferences: Array.isArray(currentUser.languagesWorkPreferences)
                        ? currentUser.languagesWorkPreferences
                        : (currentUser.languagesWorkPreferences ? [currentUser.languagesWorkPreferences] : []),
                });

                setJobPreferencesData({
                    industryJobPreferences: Array.isArray(currentUser.industryJobPreferences)
                        ? currentUser.industryJobPreferences
                        : (currentUser.industryJobPreferences ? [currentUser.industryJobPreferences] : []),
                    employmentJobPreferences: currentUser.employmentJobPreferences || '',
                    fteJobPreferences: currentUser.fteJobPreferences || '',
                    jobTypeJobPreferences: currentUser.jobTypeJobPreferences || '',
                });

                setCompanyPreferencesData({
                    companyGlobalSize: currentUser.companyGlobalSize || '',
                    companyLocalSize: currentUser.companyLocalSize || '',
                    companyRating: currentUser.companyRating || '',
                    fortune: currentUser.fortune || '',
                });

                setStudyingInfoData({
                    currentDegree: currentUser.currentDegree || '',
                    futureDegree: currentUser.futureDegree || '',
                    plannedGraduationYear: currentUser.plannedGraduationYear || '',
                    fortune: currentUser.fortune || '',
                });

                const toOccRow = (o = {}) => ({
                    major: o.major ?? '',
                    subMajor: o.subMajor ?? '',
                    minor: o.minor ?? '',
                    unit: o.unit ?? '',
                    skill: o.skill ?? '',
                });

                setOccupationData({
                    occupations:
                        Array.isArray(currentUser.occupations) && currentUser.occupations.length
                            ? currentUser.occupations.slice(0, 3).map(toOccRow)
                            : [{ major: '', subMajor: '', minor: '', unit: '', skill: '' }],
                });

            } catch (error) {
                const status = error?.response?.status;

                if (status === 403) {
                    setErrorText('Access denied: you do not have permission to view this user.');
                    setShowErrorMessage(true);
                    // optional: navigate('/users');
                    return;
                }

                console.error('Error fetching user data:', error);
                setErrorText('An error occurred while fetching user details.');
                setShowErrorMessage(true);
            } finally {
                setIsLoadingUser(false);
            }
        };

        fetchUserData();
    }, [id, navigate]);

    const validateGeneralFields = () => {
        const newErrors = {};

        if (!generalData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!generalData.familyName.trim()) newErrors.familyName = 'Last name is required';
        if (!generalData.gender) newErrors.gender = 'Gender is required';
        

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateGeneralFields()) return false; // ✅ Return false if validation fails

        try {
            const data = new FormData();

            data.append('firstName', generalData.firstName || '');
            data.append('familyName', generalData.familyName || '');
            data.append('gender', generalData.gender || '');

            if (profileImage) {
                data.append('profileImage', profileImage);
            }

            await api.put(`/api/users/${id}`, data);

            setSavedModalMessage('User General information updated successfully.');
            setShowSavedModal(true);
            return true; // ✅ Return true on success
        } catch (error) {
            console.error('Error updating general information:', error);
            setShowErrorMessage(true);
            return false; // ✅ Return false on failure
        }
    };

    const handleGeneralChange = (e) => {
        const { name, value, files } = e.target;

        console.log('handleChange called with:', name, value);

        if (name === 'profileImage' && files && files.length > 0) {
            const file = files[0];
            const fileUrl = URL.createObjectURL(file);
            setProfileImage(file);
            setGeneralData((prevData) => ({
                ...prevData,
                profileImagePreview: fileUrl, // ✅ only preview, not actual file
            }));
            return;
        }

        setGeneralData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleGeneralReset = (resetData) => {
        setGeneralData(resetData);
        setErrors({});
    };

    const isActive = generalData.activityStatus;

    const handleDeactivate = async () => {
        try {
            const endpoint = isActive ? `/api/users/${id}/deactivate` : `/api/users/${id}/activate`;
            await api.put(endpoint);
            setShowDeactivateModal(false);
            setGeneralData((prev) => ({ ...prev, activityStatus: !isActive }));
        } catch (err) {
            console.error('Error toggling account status:', err);
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/api/users/${id}`);
            setShowDeleteModal(false);
            navigate('/onboarding-user-list');
        } catch (err) {
            console.error('Error deleting account:', err);
        }
    };

    const handleRegionChange = (e) => {
        const { name, value } = e.target;

        console.log('handleChange called with:', name, value);
        
        setRegionData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleRegionSubmit = async () => {
        try {
            const payload = {
                countriesWorkPreferences: regionData.countriesWorkPreferences,
                citiesWorkPreferences: regionData.citiesWorkPreferences,
                languagesWorkPreferences: regionData.languagesWorkPreferences,
            };

            const response = await api.put(`/api/users/${id}/region`, payload);
            setSavedModalMessage('User Region information updated successfully.');
            setShowSavedModal(true);
            setShowErrorMessage(false);
        } catch (err) {
            console.error('❌ Failed to update academic info:', err);
        }
        console.log('Region form submitted');
    };

    const handleJobPreferencesChange = (e) => {
        const { name, value } = e.target;

        console.log('handleChange called with:', name, value);
        
        setJobPreferencesData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleJobPreferencesSubmit = async () => {
        try {
            const payload = {
                industryJobPreferences: jobPreferencesData.industryJobPreferences,
                employmentJobPreferences: jobPreferencesData.employmentJobPreferences,
                fteJobPreferences: jobPreferencesData.fteJobPreferences,
                jobTypeJobPreferences: jobPreferencesData.jobTypeJobPreferences,
            };

            const response = await api.put(`/api/users/${id}/job-preferences`, payload);
            setSavedModalMessage('Job Preferences information updated successfully.');
            setShowSavedModal(true);
            setShowErrorMessage(false);
        } catch (err) {
            console.error('❌ Failed to update Job Preferences info:', err);
        }
        console.log('Job Preferences submitted');
    };

    const handleCompanyPreferencesChange = (e) => {
        const { name, value } = e.target;

        console.log('handleChange called with:', name, value);
        
        setCompanyPreferencesData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleCompanyPreferencesSubmit = async () => {
        try {
            const payload = {
                companyGlobalSize: companyPreferencesData.companyGlobalSize,
                companyLocalSize: companyPreferencesData.companyLocalSize,
                companyRating: companyPreferencesData.companyRating,
                fortune: companyPreferencesData.fortune,
            };

            const response = await api.put(`/api/users/${id}/company-preferences`, payload);
            setSavedModalMessage('Company Preferences information updated successfully.');
            setShowSavedModal(true);
            setShowErrorMessage(false);
        } catch (err) {
            console.error('❌ Failed to update Company Preferences info:', err);
        }
        console.log('Company Preferences submitted');
    };

    const handleStudyingInfoChange = (e) => {
        const { name, value } = e.target;

        console.log('handleChange called with:', name, value);
        
        setStudyingInfoData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    }

    const handleStudyingInfoSubmit = async () => {
        try {
            const payload = {
                currentDegree: studyingInfoData.currentDegree,
                futureDegree: studyingInfoData.futureDegree,
                plannedGraduationYear: studyingInfoData.plannedGraduationYear,
            };

            const response = await api.put(`/api/users/${id}/studying-info`, payload);
            setSavedModalMessage('Studying information updated successfully.');
            setShowSavedModal(true);
            setShowErrorMessage(false);
        } catch (err) {
            console.error('❌ Failed to update Studying info:', err);
        }
        console.log('Company Preferences submitted');
    };

    const handleOccupationChange = (e) => {
        const { name, value } = e.target;

        console.log('handleChange called with:', name, value);
        
        setOccupationData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    }

    const handleOccupationSubmit = async (e, listOverride) => {
        if (e?.preventDefault) e.preventDefault();
        try {
            if (occupationRef.current?.validate && !occupationRef.current.validate()) {
                return false;
            }
            const payload = { occupations: listOverride ?? occupationData.occupations };         
            await api.put(`/api/users/${id}/occupations`, payload);                    
            setSavedModalMessage('Occupations updated successfully.');
            setShowSavedModal(true);
            setShowErrorMessage(false);
            return true;
        } catch (err) {
            console.error('❌ Failed to update occupations:', err);
            setShowErrorMessage(true);
            return false;
        }
    };

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleGeneralSubmit}>
                                                            <UserAddGeneral
                                                                formData={{ 
                                                                    ...generalData
                                                                }}
                                                                profileImage={generalData.profileImagePreview}
                                                                handleChange={handleGeneralChange}
                                                                handleGeneralSubmit={handleGeneralSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                activityStatus={activityStatus}
                                                                handleReset={handleGeneralReset}
                                                                companies={companies}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={userNavItems} />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleRegionSubmit}>
                                                            <UserAddRegion
                                                                formData={regionData}
                                                                handleChange={handleRegionChange}
                                                                handleRegionSubmit={handleRegionSubmit}
                                                                errors={errors}
                                                                ref={regionRef}
                                                                isEditPage={true}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleJobPreferencesSubmit}>
                                                            <UserAddJob
                                                                formData={jobPreferencesData}
                                                                handleChange={handleJobPreferencesChange}
                                                                handleJobPreferencesSubmit={handleJobPreferencesSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleOccupationSubmit}>
                                                            <UserAddOccupation
                                                                ref={occupationRef}
                                                                formData={occupationData}
                                                                // ✅ we still accept updates, but now they only come on Save (no flashing)
                                                                onOccupationsChange={(list) =>
                                                                    setOccupationData((prev) => ({ ...prev, occupations: list }))
                                                                }
                                                                handleOccupationSubmit={handleOccupationSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleCompanyPreferencesSubmit}>
                                                            <UserAddCompany
                                                                formData={companyPreferencesData}
                                                                handleChange={handleCompanyPreferencesChange}
                                                                handleCompanyPreferencesSubmit={handleCompanyPreferencesSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleStudyingInfoSubmit}>
                                                            <UserAddStudying
                                                                formData={studyingInfoData}
                                                                handleChange={handleStudyingInfoChange}
                                                                handleStudyingInfoSubmit={handleStudyingInfoSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Link to="/user-add" className="btn btn-outline-primary">
                                Add new user
                            </Link>
                            <Button variant="outline-secondary" onClick={() => setShowDeactivateModal(true)}>{isActive ? 'Deactivate account' : 'Activate account'}</Button>
                            <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete account</Button>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isActive ? 'Deactivate account' : 'Activate account'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{isActive
                        ? 'Are you sure you want to deactivate this account?'
                        : 'Are you sure you want to reactivate this account?'
                    }</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeactivateModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleDeactivate}>{isActive ? 'Deactivate' : 'Activate'}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete account</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to permanently delete this account? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDelete}>Delete account</Button>
                </Modal.Footer>
            </Modal>

            {/* Saved info Modal */}
            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">{savedModalTitle}</h2>
                    <p className="text-center">{savedModalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserEditWorkPreferences;
